import React, { useState, useRef, useEffect, useCallback } from 'react';

const ChatInput = ({ onSend, isThinking, onFileUpload }) => {
  const [inputValue, setInputValue] = useState('');
  const [textareaHeight, setTextareaHeight] = useState('auto');
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);

  // Check for speech recognition support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition);

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputValue(prev => prev + transcript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Global keyboard shortcut: Cmd/Ctrl + K to focus input
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K to focus input
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        textareaRef.current?.focus();
      }
      // Escape to clear input
      if (e.key === 'Escape' && document.activeElement === textareaRef.current) {
        setInputValue('');
        textareaRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-resize textarea based on content with responsive max height
  useEffect(() => {
    if (textareaRef.current) {
      setTextareaHeight('auto');
      const scrollHeight = textareaRef.current.scrollHeight;
      const isMobile = window.innerWidth < 640;
      const maxHeight = isMobile ? 96 : 140;
      const newHeight = Math.min(scrollHeight, maxHeight);
      setTextareaHeight(`${newHeight}px`);
    }
  }, [inputValue]);

  const handleSend = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      onSend(inputValue.trim());
      setInputValue('');
      setTextareaHeight('auto');
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0 && typeof onFileUpload === 'function') {
      onFileUpload(files);
    }
  };

  const toggleVoiceInput = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
      }
    }
  }, [isListening]);

  return (
    <form onSubmit={handleSend} className="w-full">
      {/* Keyboard shortcut hint */}
      <div className="flex items-center justify-end mb-1">
        <span className="text-[10px] text-gray-400 hidden sm:inline">
          Press <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] font-mono">⌘K</kbd> to focus
        </span>
      </div>

      <div className="flex items-end gap-2 sm:gap-3">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            className="w-full resize-none border border-gray-200 rounded-xl px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white text-gray-800 text-base sm:text-base overflow-y-auto"
            style={{ height: textareaHeight, minHeight: '40px' }}
            placeholder={isListening ? 'Listening...' : 'Type your message...'}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            disabled={isThinking}
            inputMode="text"
            enterKeyHint="send"
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck={true}
            aria-label="Chat message input"
          />

          {/* Voice input indicator */}
          {isListening && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <span className="flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            </div>
          )}
        </div>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.rtf"
        />

        {/* Voice input button */}
        {speechSupported && (
          <button
            type="button"
            className={`w-10 h-10 sm:w-11 sm:h-11 rounded-lg flex items-center justify-center shadow transition flex-shrink-0 touch-manipulation ${isListening
              ? 'bg-red-500 text-white animate-pulse'
              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            disabled={isThinking}
            onClick={toggleVoiceInput}
            aria-label={isListening ? 'Stop listening' : 'Voice input'}
            title={isListening ? 'Stop listening' : 'Voice input'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        )}

        {/* Upload button */}
        <button
          type="button"
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-gray-200 text-gray-600 flex items-center justify-center shadow hover:bg-gray-300 transition disabled:opacity-40 flex-shrink-0 touch-manipulation"
          disabled={isThinking}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Upload CV"
          title="Upload CV"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>

        {/* Send button */}
        <button
          type="submit"
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-blue-600 text-white flex items-center justify-center shadow hover:bg-blue-700 transition disabled:opacity-40 flex-shrink-0 touch-manipulation"
          disabled={isThinking || !inputValue.trim()}
          aria-label="Send message"
          title="Send message"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
    </form>
  );
};

export default ChatInput;

